<template>
  <div class="wallet-section">
    <div v-if="isLoading" class="loading-container">
      <div class="loading-spinner">
        <i class="fa fa-spinner fa-spin fa-2x"></i>
      </div>
      <p>Loading wallet information...</p>
    </div>

    <div v-else-if="error" class="error-container">
      <div class="error-icon">
        <i class="fa fa-exclamation-circle fa-2x"></i>
      </div>
      <p>{{ error }}</p>
      <button class="btn btn-primary" @click="fetchWallets">Try Again</button>
    </div>

    <div v-else-if="wallets.length === 0" class="no-wallet-notice">
      <div class="notice-card">
        <div class="notice-icon">
          <i class="fa fa-info-circle"></i>
        </div>
        <div class="notice-content">
          <h3>No Wallet Available</h3>
          <p>Your account does not have a wallet set up yet. Wallets are used to manage prepaid balances for bookings.</p>
          <p class="notice-hint">If you believe you should have access to a wallet, please contact your account manager.</p>
        </div>
      </div>
    </div>

    <div v-else class="wallet-content">
      <!-- Wallet Cards -->
      <div class="wallet-cards">
        <div
          v-for="(wallet, index) in wallets"
          :key="index"
          class="wallet-card"
          :class="{ 'wallet-card-active': selectedWalletIndex === index }"
          @click="selectWallet(index)"
        >
          <div class="wallet-card-header">
            <span class="wallet-name">{{ getWalletName(index) }}</span>
            <span class="wallet-currency">{{ wallet.walletCurrency }}</span>
          </div>
          <div class="wallet-balance">
            {{ formatCurrency(wallet.walletBalance, wallet.walletCurrency) }}
          </div>
          <div class="wallet-card-footer" v-if="wallets.length > 1">
            <span class="view-details">
              {{ selectedWalletIndex === index ? 'Viewing details' : 'Click to view details' }}
            </span>
          </div>
        </div>
      </div>

      <!-- Transaction Details -->
      <div v-if="selectedWallet" class="transactions-section">
        <!-- Credit History (Top-ups) -->
        <div class="card">
          <div class="card-header">
            <i class="fa fa-plus-circle card-header--icon"></i>
            <span class="card-header--title">Credit History (Top-ups)</span>
          </div>
          <div class="card-content">
            <div v-if="selectedWallet.creditHistory && selectedWallet.creditHistory.length > 0" class="table-container">
            <table class="transactions-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Amount Added</th>
                  <th>Date</th>
                  <th>Balance After</th>
                </tr>
              </thead>
              <tbody>
                <tr 
                  v-for="transaction in selectedWallet.creditHistory" 
                  :key="transaction.transactionId"
                  :data-transaction-id="transaction.transactionId"
                  class="transaction-row"
                >
                  <td>
                    <span class="transaction-type type-topup">{{ transaction.transactionType }}</span>
                  </td>
                  <td class="amount-added">
                    +{{ formatCurrency(transaction.amountAdded, selectedWallet.walletCurrency) }}
                  </td>
                  <td>{{ formatDate(transaction.transactionDate) }}</td>
                  <td>{{ formatCurrency(transaction.walletBalance, selectedWallet.walletCurrency) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
            <div v-else class="no-transactions">
              <p>No credit history available.</p>
            </div>
          </div>
        </div>

        <!-- Last Transactions -->
        <div class="card">
          <div class="card-header">
            <i class="fa fa-exchange card-header--icon"></i>
            <span class="card-header--title">Recent Transactions</span>
          </div>
          <div class="card-content">
            <div v-if="selectedWallet.lastTransactions && selectedWallet.lastTransactions.length > 0" class="table-container">
            <table class="transactions-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Date</th>
                  <th>Balance After</th>
                </tr>
              </thead>
              <tbody>
                <tr 
                  v-for="transaction in selectedWallet.lastTransactions" 
                  :key="transaction.transactionId"
                  :data-transaction-id="transaction.transactionId"
                  class="transaction-row"
                >
                  <td>
                    <span 
                      class="transaction-type" 
                      :class="getTransactionTypeClass(transaction.transactionType)"
                    >
                      {{ transaction.transactionType }}
                    </span>
                  </td>
                  <td :class="getAmountClass(transaction)">
                    {{ formatTransactionAmount(transaction, selectedWallet.walletCurrency) }}
                  </td>
                  <td>{{ formatDate(transaction.transactionDate) }}</td>
                  <td>{{ formatCurrency(transaction.walletBalance, selectedWallet.walletCurrency) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
            <div v-else class="no-transactions">
              <p>No recent transactions available.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Api from '@/services/ApiGateway'
import { mapGetters } from 'vuex'

export default {
  name: 'WalletPage',
  data() {
    return {
      wallets: [],
      selectedWalletIndex: 0,
      isLoading: true,
      error: null
    }
  },
  computed: {
    ...mapGetters({
      impersonateUser: 'authentication/impersonateUser'
    }),
    selectedWallet() {
      return this.wallets[this.selectedWalletIndex] || null
    }
  },
  watch: {
    impersonateUser() {
      this.fetchWallets()
    }
  },
  created() {
    this.fetchWallets()
  },
  methods: {
    async fetchWallets() {
      this.isLoading = true
      this.error = null
      try {
        const response = await Api.getWallets(this.impersonateUser)
        this.wallets = response.wallets || []
        // Auto-select first wallet
        if (this.wallets.length > 0) {
          this.selectedWalletIndex = 0
        }
      } catch (err) {
        this.error = err.message || 'Failed to load wallet information'
        console.error('Error fetching wallets:', err)
      } finally {
        this.isLoading = false
      }
    },
    selectWallet(index) {
      this.selectedWalletIndex = index
    },
    getWalletName(index) {
      if (this.wallets.length === 1) {
        return 'Wallet'
      }
      return `Wallet #${index + 1}`
    },
    formatCurrency(amountInCents, currency) {
      const amount = amountInCents / 100
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount)
    },
    formatDate(dateString) {
      const date = new Date(dateString)
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date)
    },
    formatTransactionAmount(transaction, currency) {
      if (transaction.amountDeducted > 0) {
        return '-' + this.formatCurrency(transaction.amountDeducted, currency)
      }
      if (transaction.amountAdded > 0) {
        return '+' + this.formatCurrency(transaction.amountAdded, currency)
      }
      return this.formatCurrency(0, currency)
    },
    getTransactionTypeClass(type) {
      switch (type) {
        case 'PAYMENT':
          return 'type-payment'
        case 'REFUND':
          return 'type-refund'
        case 'TOPUP':
          return 'type-topup'
        default:
          return ''
      }
    },
    getAmountClass(transaction) {
      if (transaction.amountDeducted > 0) {
        return 'amount-deducted'
      }
      if (transaction.amountAdded > 0) {
        return 'amount-added'
      }
      return ''
    }
  }
}
</script>

<style scoped>
.wallet-section {
  padding: 1rem 0;
}

.page-title {
  margin: 0 0 1.5rem 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--gray-darker);
}

.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  color: var(--gray);
}

.no-wallet-notice {
  margin-top: 1rem;
}

.notice-card {
  display: flex;
  align-items: flex-start;
  gap: 1.25rem;
  background: white;
  border-radius: 12px;
  padding: 1.5rem 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-left: 4px solid var(--brand-primary);
}

.notice-icon {
  font-size: 1.75rem;
  color: var(--brand-primary);
  flex-shrink: 0;
  padding-top: 0.25rem;
}

.notice-content h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--gray-darker);
}

.notice-content p {
  margin: 0 0 0.5rem 0;
  color: var(--gray-dark);
  line-height: 1.5;
}

.notice-content p:last-child {
  margin-bottom: 0;
}

.notice-hint {
  font-size: 0.9rem;
  color: var(--gray);
}

.loading-spinner,
.error-icon {
  margin-bottom: 1rem;
  color: var(--brand-primary);
}

.error-icon {
  color: var(--brand-warning);
}

.btn-primary {
  background: var(--brand-primary);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 1rem;
  transition: background 0.15s;
}

.btn-primary:hover {
  background: var(--brand-primary-dark);
}

.wallet-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.wallet-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.wallet-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  min-width: 280px;
  cursor: pointer;
  border: 1px solid var(--gray-light);
}

.wallet-card:hover {
  border-color: var(--gray-d4);
}

.wallet-card-active {
  border-color: var(--brand-primary);
  background: var(--brand-primary-extra-light);
}

.wallet-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.wallet-name {
  font-weight: 600;
  font-size: 1.1rem;
  color: var(--gray-darker);
}

.wallet-currency {
  background: var(--brand-primary-extra-light);
  color: var(--brand-primary-dark);
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
}

.wallet-balance {
  font-size: 2rem;
  font-weight: 700;
  color: var(--brand-primary);
  margin-bottom: 0.5rem;
}

.wallet-card-footer {
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid var(--gray-light);
}

.view-details {
  font-size: 0.85rem;
  color: var(--gray);
}

.transactions-section {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.table-container {
  overflow-x: auto;
}

.transactions-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.95rem;
}

.transactions-table th,
.transactions-table td {
  padding: 0.875rem 1rem;
  text-align: left;
  border-bottom: 1px solid var(--gray-light);
}

.transactions-table th {
  background: var(--gray-lighter);
  font-weight: 600;
  color: var(--gray-dark);
  white-space: nowrap;
}

.transactions-table tbody tr:hover {
  background: var(--gray-lighter);
}

.transaction-id {
  font-family: monospace;
  font-size: 0.9rem;
  color: var(--gray-dark);
}

.transaction-type {
  display: inline-block;
  padding: 0.25rem 0.625rem;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
}

.type-payment {
  background: var(--secondary-extra-light);
  color: var(--brand-secondary-dark);
}

.type-refund {
  background: var(--purple-extra-light);
  color: var(--purple);
}

.type-topup {
  background: var(--brand-primary-extra-light);
  color: var(--brand-primary-dark);
}

.amount-added {
  color: var(--brand-success);
  font-weight: 600;
}

.amount-deducted {
  color: var(--brand-warning);
  font-weight: 600;
}

.no-transactions {
  padding: 2rem;
  text-align: center;
  color: var(--gray);
  background: var(--gray-lighter);
  border-radius: 8px;
}

@media (max-width: 768px) {
  .wallet-cards {
    flex-direction: column;
  }

  .wallet-card {
    min-width: 100%;
  }

  .transactions-table {
    font-size: 0.85rem;
  }

  .transactions-table th,
  .transactions-table td {
    padding: 0.625rem 0.5rem;
  }
}
</style>
